/* eslint-disable max-lines */
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {gql} from "@apollo/client";
import {useTranslation} from "react-i18next";
import {
  ChallengeScenarioActivationMethod,
  ChallengeScenarioAfterAction,
  ChallengeScenarioAfterActionTarget,
  ScenarioInput,
} from "../../schema";
import {Scenario} from "../../types";
import {useFormState} from "../../hooks/useFormState";
import classes from "./ScenarioCustomizeForm.module.css";
import ChallengeAction from "./ChallengeAction";
import EditableInput from "./EditableInput";
import InputWithReset from "../../ui/inputwithreset";
import WithTooltip from "../../ui/tooltip";
import {HelpCircleIcon} from "../../ui/icons";
import PhrasesEdit from "./PhrasesEdit";
import {INPUT_PHRASE_MAX_LENGTH} from "../../settings";
import Link from "../../ui/link";
import Select from "../../ui/select";
import EditScenarioActivationMethodModal from "./EditScenarioActivationMethodModal";
import {useModal} from "../ModalProvider";

type props = {
  scenario: Scenario;
  onSave: (data: ScenarioInput) => void;
  onDelete: () => void;
  challengeScenarios?: Array<{id: string; name: string}>;
  scenarios?: Scenario[];
  showManualMaxScore?: boolean;
};

type formState = {
  name: string;
  triggerPhrases: string[];
  afterAction: ChallengeScenarioAfterAction;
  afterActionTarget: ChallengeScenarioAfterActionTarget;
  activationMethod: ChallengeScenarioActivationMethod;
  nextScenario: string | null;
  manualMaxScore?: number | null | "";
};

export default function ScenarioCustomizeForm({
  scenario,
  scenarios,
  showManualMaxScore = false,
  onSave,
  onDelete,
}: props) {
  const {t} = useTranslation();

  const filteredScenarios = useMemo(
    () => scenarios?.filter((currScenario) => !currScenario.isMain && currScenario.id !== scenario.id),
    [scenarios, scenario],
  );

  const currentScenarioId = scenario.id;
  const {add: addModal} = useModal();

  const [currentMethod, setCurrentMethod] = useState(scenario.activationMethod);
  const onChangeRef = useRef<(data: ScenarioInput) => void>();
  const formState = useFormState<formState>({
    initialValues: {
      name: scenario.name,
      triggerPhrases: scenario.triggerPhrases,
      afterAction: scenario.afterAction,
      afterActionTarget: scenario.afterActionTarget,
      activationMethod: scenario.activationMethod,
      nextScenario: scenario.nextScenario || filteredScenarios?.[0]?.id || null,
      manualMaxScore: scenario.manualMaxScore,
    },
    preventDefault: true,
    onChange: (data) => {
      const activationMethodChanged = data.activationMethod !== prevFormValuesRef.current.activationMethod;
      prevFormValuesRef.current = data;

      const prepareData: ScenarioInput = {
        ...data,
        manualMaxScore: data.manualMaxScore !== "" ? data.manualMaxScore : null,
      };

      if (activationMethodChanged) {
        onChangeRef.current?.(prepareData);
      } else {
        onSave(prepareData);
      }
    },
  });

  const prevFormValuesRef = useRef(formState.values);

  const onActivationMethodChange = useCallback(
    (data: ScenarioInput) => {
      const selectedMethod = data.activationMethod;
      if (selectedMethod === currentMethod) {
        return;
      }
      if (!scenarios || !currentScenarioId) {
        return undefined;
      }
      const scenariosWithBranchesLeadsToCurrent = scenarios
        .filter((scenario) => {
          return scenario.actions?.some((action) => {
            if (action.__typename === "ChoiceUserInputAction") {
              return action.branches.some((branch) => branch.targetScenarioId === currentScenarioId);
            }
            return null;
          });
        })
        .map(({id, name, isMain}) => ({id, name: isMain ? t("editorChallengeEdit.scenario.main") : name}));
      if (scenariosWithBranchesLeadsToCurrent.length === 0) {
        setCurrentMethod(selectedMethod ?? currentMethod);
        onSave(data);
        return;
      }

      const handleConfirm = () => {
        setCurrentMethod(selectedMethod as ChallengeScenarioActivationMethod);
        onSave(data);
        modal.remove();
      };
      const onCancel = () => {
        formState.setValues({activationMethod: currentMethod});
        modal.remove();
      };

      const modal = addModal({
        header: <EditScenarioActivationMethodModal.Header />,
        content: (
          <EditScenarioActivationMethodModal.Content
            scenariosWithBranchesLeadsToCurrent={scenariosWithBranchesLeadsToCurrent}
          />
        ),
        footer: <EditScenarioActivationMethodModal.Footer onConfirm={handleConfirm} onCancel={onCancel} />,
        stopCloseOnOuterClick: true,
        hideCloseButton: true,
      });
    },
    [currentScenarioId, addModal, scenarios, onSave, formState, currentMethod, t],
  );

  useEffect(() => {
    onChangeRef.current = onActivationMethodChange;
  }, [onActivationMethodChange]);

  useEffect(() => {
    prevFormValuesRef.current = {
      ...scenario,
      nextScenario: scenario.nextScenario || filteredScenarios?.[0]?.id || null,
    };
  }, [scenario, filteredScenarios]);

  const updatePhrases = useCallback(
    (triggerPhrases: string[]) => {
      formState.setValues({triggerPhrases});
    },
    [formState],
  );

  const analogTextsTextareaProps = useMemo(
    () => ({
      maxLength: INPUT_PHRASE_MAX_LENGTH,
      rows: 4,
      autoComplete: "off",
      placeholder: t("components.ScenarioCustomizeForm.triggerPhrases.placeholder"),
    }),
    [t],
  );

  return (
    <form method="post" onSubmit={formState.submitHandler} autoComplete="off">
      <div className={classes.formItem}>
        <label className={classes.label}>{t("components.ScenarioCustomizeForm.name")}</label>
        <EditableInput.Wrapper multiline className={classes.nameWrapper}>
          <EditableInput
            type="text"
            className={classes.name}
            buttonClassName={classes.nameButtons}
            name="name"
            defaultValue={formState.values.name}
            onChange={formState.changeHandler}
            autoComplete="off"
            maxLength={60}
          />
        </EditableInput.Wrapper>
      </div>

      <div className={classes.formItem}>
        <label className={classes.label}>{t("components.ScenarioCustomizeForm.activationMethod")}</label>
        <WithTooltip
          as="span"
          className={classes.help}
          helpText={t("components.ScenarioCustomizeForm.activationMethodHelp")}
        >
          <Select name="activationMethod" value={formState.values.activationMethod} onChange={formState.changeHandler}>
            <option value={ChallengeScenarioActivationMethod.TRIGGER_PHRASE}>
              {t("types.ScenarioActivationMethod.TRIGGER_PHRASE")}
            </option>
            {/*<option value={ChallengeScenarioActivationMethod.AVATAR_PHRASE}>
              {t("types.ScenarioActivationMethod.AVATAR_PHRASE")}
            </option>*/}
            <option value={ChallengeScenarioActivationMethod.USER_INPUT}>
              {t("types.ScenarioActivationMethod.USER_INPUT")}
            </option>
          </Select>
        </WithTooltip>
      </div>

      {scenario.activationMethod === ChallengeScenarioActivationMethod.TRIGGER_PHRASE && (
        <div className={classes.formItem}>
          <label className={classes.label} htmlFor="triggerPhrases">
            {t("components.ScenarioCustomizeForm.triggerPhrases.title")}{" "}
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ScenarioCustomizeForm.triggerPhrases.help")}
            >
              <HelpCircleIcon />
            </WithTooltip>
          </label>
          <PhrasesEdit
            value={formState.values.triggerPhrases}
            onValueChange={updatePhrases}
            textareaProps={analogTextsTextareaProps}
          />
        </div>
      )}

      <div className={classes.formItem}>
        <label className={classes.label}>
          {t("components.ScenarioCustomizeForm.afterAction")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ScenarioCustomizeForm.afterActionDescription")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <WithTooltip
          as="span"
          className={classes.help}
          helpText={t("components.ScenarioCustomizeForm.afterActionHelp")}
        >
          <Select name="afterAction" value={formState.values.afterAction} onChange={formState.changeHandler}>
            {/*(scenario.activationMethod !== ChallengeScenarioActivationMethod.AVATAR_PHRASE ||
              (scenario.activationMethod === ChallengeScenarioActivationMethod.AVATAR_PHRASE &&
                scenario.afterActionTarget === ChallengeScenarioAfterActionTarget.MAIN)) && (
              <option value={ChallengeScenarioAfterAction.RETURN}>{t("types.ScenarioAfterAction.RETURN")}</option>
            )*/}
            <option value={ChallengeScenarioAfterAction.RETURN}>{t("types.ScenarioAfterAction.RETURN")}</option>
            <option value={ChallengeScenarioAfterAction.NEXT}>{t("types.ScenarioAfterAction.NEXT")}</option>
            {filteredScenarios?.[0]?.id && (
              <option value={ChallengeScenarioAfterAction.NEXT_SCENARIO}>
                {t("types.ScenarioAfterAction.NEXT_SCENARIO")}
              </option>
            )}
            <option value={ChallengeScenarioAfterAction.INTERRUPT}>{t("types.ScenarioAfterAction.INTERRUPT")}</option>
            <option value={ChallengeScenarioAfterAction.FINISH}>{t("types.ScenarioAfterAction.FINISH")}</option>
          </Select>
        </WithTooltip>
      </div>

      {formState.values.afterAction === ChallengeScenarioAfterAction.NEXT_SCENARIO && filteredScenarios?.[0]?.id && (
        <div className={classes.formItem}>
          <label className={classes.label}>{t("components.ScenarioCustomizeForm.nextScenario")}</label>
          <Select
            name="nextScenario"
            defaultValue={formState.values.nextScenario || filteredScenarios?.[0]?.id}
            onChange={formState.changeHandler}
          >
            {formState.values.afterAction === ChallengeScenarioAfterAction.NEXT_SCENARIO &&
              filteredScenarios?.map((currScenario) => (
                <option key={currScenario.id} value={currScenario.id}>
                  {currScenario.name}
                </option>
              ))}
          </Select>
        </div>
      )}

      {(formState.values.afterAction === ChallengeScenarioAfterAction.RETURN ||
        formState.values.afterAction === ChallengeScenarioAfterAction.NEXT) && (
        <div className={classes.formItem}>
          <label className={classes.label}>{t("components.ScenarioCustomizeForm.afterActionTarget")}</label>
          <WithTooltip
            as="span"
            className={classes.help}
            helpText={t("components.ScenarioCustomizeForm.afterActionTargetHelp")}
          >
            <Select
              name="afterActionTarget"
              value={formState.values.afterActionTarget}
              onChange={formState.changeHandler}
            >
              <option value={ChallengeScenarioAfterActionTarget.MAIN}>
                {t("types.ScenarioAfterActionTarget.MAIN")}
              </option>
              <option value={ChallengeScenarioAfterActionTarget.PREVIOUS}>
                {t("types.ScenarioAfterActionTarget.PREVIOUS")}
              </option>
            </Select>
          </WithTooltip>
        </div>
      )}

      {showManualMaxScore && (
        <div className={classes.formItem}>
          <label className={classes.label} htmlFor="manualMaxScore">
            {t("components.ScenarioCustomizeForm.manualMaxScore.label")}{" "}
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ScenarioCustomizeForm.manualMaxScore.helpText")}
            >
              <HelpCircleIcon />
            </WithTooltip>
          </label>
          <InputWithReset
            min={0}
            type="number"
            displaySize="sm"
            id="manualMaxScore"
            name="manualMaxScore"
            value={formState.values.manualMaxScore ?? ""}
            onChange={formState.changeHandler}
            onButtonClick={() => formState.setValues({manualMaxScore: null})}
          />
        </div>
      )}

      <div className={classes.formItem}>
        <Link type="button" color="danger" onClick={onDelete}>
          {t("components.ScenarioCustomizeForm.deleteScenario")}
        </Link>
      </div>
    </form>
  );
}

ScenarioCustomizeForm.fragments = {
  root: gql`
    fragment ScenarioCustomizeForm on Scenario {
      __typename
      id
      name
      manualMaxScore
      isMain
      triggerPhrases
      afterAction
      afterActionTarget
      activationMethod
      nextScenario
      actions {
        id
        ...ChallengeAction
      }
    }

    ${ChallengeAction.fragments.root}
  `,
};

